import React, { useState, useEffect } from 'react';
import { getDoctorsByClinic, updateDoctorStatus } from '../../services/api';
import DoctorEditModal from './DoctorEditModal';
import AddDoctorModal from './AddDoctorModal';

const DoctorsList = ({ clinicId }) => {
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingDoctor, setEditingDoctor] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddDoctorModalOpen, setIsAddDoctorModalOpen] = useState(false);

    useEffect(() => {
        fetchDoctors();
    }, [clinicId]);

    const fetchDoctors = async () => {
        try {
            setLoading(true);
            const response = await getDoctorsByClinic(clinicId);
            if (response.success) {
                setDoctors(Array.isArray(response.data) ? response.data : [response.data]);
            } else {
                setError(response.message || 'Failed to fetch doctors');
            }
        } catch (err) {
            setError('Failed to fetch doctors. Please try again.');
            console.error('Error fetching doctors:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (doctorId, doctorUserId, newStatus) => {
        try {
            const response = await updateDoctorStatus(doctorId, {
                doctorUserId,
                clinicId,
                status: newStatus
            });

            if (response.success) {
                // Refresh the doctors list
                fetchDoctors();
            } else {
                setError(response.message || 'Failed to update doctor status');
            }
        } catch (err) {
            setError('Failed to update doctor status. Please try again.');
            console.error('Error updating doctor status:', err);
        }
    };

    const handleEditDoctor = (doctor) => {
        setEditingDoctor(doctor);
        setIsEditModalOpen(true);
    };

    const handleEditSuccess = () => {
        fetchDoctors(); // Refresh the list after successful edit
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setEditingDoctor(null);
    };

    const handleAddDoctorSuccess = () => {
        fetchDoctors(); // Refresh the list after successful addition
    };

    const handleCloseAddDoctorModal = () => {
        setIsAddDoctorModalOpen(false);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
                {error}
            </div>
        );
    }

    return (
        <div>
            {/* Header with Add Doctor Button */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">Doctors</h2>
                <button
                    onClick={() => setIsAddDoctorModalOpen(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                    Add New Doctor
                </button>
            </div>

            {doctors.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-gray-500 text-lg">No doctors found for this clinic.</p>
                    <button
                        onClick={() => setIsAddDoctorModalOpen(true)}
                        className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                        Add First Doctor
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                    {doctors.map((doctor) => (
                        <div key={doctor.id} className="bg-white rounded-lg shadow-md p-6">
                            {/* Doctor Header */}
                            <div className="flex flex-col sm:flex-row items-start mb-4">
                                {/* Profile Image */}
                                <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-6">
                                    <img
                                        className="w-24 h-24 rounded-full object-cover"
                                        src={doctor.profileImageUrl || 'https://via.placeholder.com/150'}
                                        alt={`Dr. ${doctor.firstName} ${doctor.lastName}`}
                                    />
                                </div>

                                {/* Info and Actions */}
                                <div className="flex-grow">
                                    <div className="flex flex-col sm:flex-row justify-between items-start">
                                        <div>
                                            <h3 className="text-xl font-semibold text-gray-900">
                                                Dr. {doctor.firstName} {doctor.lastName}
                                            </h3>
                                            <p className="text-gray-600">{doctor.specialization}</p>
                                            <p className="text-sm text-gray-500">{doctor.experienceYears} years of experience</p>
                                        </div>
                                        <div className="flex space-x-2 mt-4 sm:mt-0">
                                            <button
                                                onClick={() => handleEditDoctor(doctor)}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleStatusChange(doctor.id, doctor.userId, 'INACTIVE')}
                                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                            >
                                                Deactivate
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Doctor Bio */}
                            <p className="text-gray-700 mb-6">{doctor.bio}</p>

                            {/* Qualifications */}
                            <div className="mb-6">
                                <h4 className="text-lg font-semibold text-gray-900 mb-3">Qualifications</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {doctor.qualifications?.map((qual) => (
                                        <div key={qual.id} className="bg-gray-50 p-3 rounded-lg">
                                            <p className="font-medium text-gray-900">{qual.degree}</p>
                                            <p className="text-sm text-gray-600">{qual.institution}</p>
                                            <p className="text-sm text-gray-500">{qual.year}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Achievements */}
                            {doctor.achievements?.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Achievements</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {doctor.achievements.map((ach) => (
                                            <div key={ach.id} className="bg-gray-50 p-3 rounded-lg">
                                                <p className="font-medium text-gray-900">{ach.title}</p>
                                                <p className="text-sm text-gray-600">{ach.description}</p>
                                                <p className="text-sm text-gray-500">{ach.year}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Certifications */}
                            {doctor.certifications?.length > 0 && (
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Certifications</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {doctor.certifications.map((cert) => (
                                            <div key={cert.id} className="bg-gray-50 p-3 rounded-lg">
                                                <p className="font-medium text-gray-900">{cert.name}</p>
                                                <p className="text-sm text-gray-600">{cert.issuingBody}</p>
                                                <p className="text-sm text-gray-500">{cert.year}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Edit Modal */}
            <DoctorEditModal
                doctor={editingDoctor}
                clinicId={clinicId}
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                onSuccess={handleEditSuccess}
            />

            {/* Add Doctor Modal */}
            <AddDoctorModal
                clinicId={clinicId}
                isOpen={isAddDoctorModalOpen}
                onClose={handleCloseAddDoctorModal}
                onSuccess={handleAddDoctorSuccess}
            />
        </div>
    );
};

export default DoctorsList; 